// AGC: VoiceWizardConfirmation — step-by-step wizard for voice field confirmation
// AGC: Each field gets its own card, navigated with Back/Next + swipe gestures

import React, { useState, useRef, useCallback } from 'react'
import { ChevronLeft, ChevronRight, ArrowLeft, Edit3 } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { VoiceProcessingResponse } from '../../hooks/useVoiceRecording'
import type { StockTreatmentMode } from '../../utils/financialTreatmentPolicy'
import {
  FieldMicButton,
  TitleField,
  CategoryField,
  FieldSelector,
  PriorityField,
  DateField,
  AssigneeField,
  MachinerySelector,
  InputsSelector,
  BrandField,
  QuantityField,
  GenericField,
  WIZARD_STEP_ORDER,
  SYSTEM_SLOTS,
  MANDATORY_FIELDS,
  EXPENSE_HIDDEN_SLOTS,
  type VoiceFieldProps,
} from './fields'
import { VoiceSinglePageConfirmation } from './VoiceSinglePageConfirmation'

// AGC: Props for wizard confirmation — mirrors VoiceSinglePageConfirmationProps
interface VoiceWizardConfirmationProps {
  lastResponse: VoiceProcessingResponse
  inputValues: Record<string, string>
  setInputValues: React.Dispatch<React.SetStateAction<Record<string, string>>>
  onConfirmFields?: (mergedData: Record<string, string | number | null>) => void
  onFieldSuggestionSelect?: (fieldName: string, value: string, label: string) => void
  onClose: () => void
  /** AGC: Switch to single-page mode */
  onModeSwitch: () => void
  onPermissionBlocked: () => void
  // AGC: Farm selection for field creation
  farms: Array<{ id: string; name: string; plots?: any[] }>
  selectedFarmId: string | null
  setSelectedFarmId: (id: string) => void
  // AGC: Financial treatment
  stockToggle: boolean
  setStockToggle: React.Dispatch<React.SetStateAction<boolean>>
  customFinancialCategories: Array<{ id: string; label: string; stockTreatment?: StockTreatmentMode }>
  onAddCustomCategory: (label: string) => void
}

// AGC: A single wizard step descriptor
interface WizardStep {
  fieldName: string
  type: 'transcription' | 'field' | 'summary'
  label: string
}

// AGC: Dispatch field name to the right renderer component (same logic as single-page)
function renderField(
  fieldName: string,
  info: any,
  commonProps: VoiceFieldProps,
  onFieldSuggestionSelect?: (fieldName: string, value: string, label: string) => void,
  customFinancialCategories?: Array<{ id: string; label: string; stockTreatment?: StockTreatmentMode }>,
  onAddCustomCategory?: (label: string) => void,
): React.ReactNode {
  // AGC: Title / Name — both use TitleField (tasks use 'title', vehicles/fields use 'name')
  if ((fieldName === 'title' && info.isTitleInput) || fieldName === 'name') {
    return <TitleField {...commonProps} />
  }
  // AGC: Field/lot selector for tasks
  if (fieldName === 'field' && info.isFieldSelector) {
    return <FieldSelector {...commonProps} />
  }
  // AGC: Category selector — tasks, expenses, revenues, AND vehicles
  if (fieldName === 'category' && (info.isCategorySelector || info.suggestions?.length > 0)) {
    return (
      <CategoryField
        {...commonProps}
        customFinancialCategories={customFinancialCategories ?? []}
        onAddCustomCategory={onAddCustomCategory ?? (() => {})}
      />
    )
  }
  if (fieldName === 'priority' && info.isPrioritySelector) {
    return <PriorityField {...commonProps} />
  }
  if (fieldName === 'dueDate' && (info.isDueDateSelector || info.inputType === 'date')) {
    return <DateField {...commonProps} />
  }
  if (fieldName === 'assignee' && info.isAssigneeSelector) {
    return <AssigneeField {...commonProps} />
  }
  if (fieldName === 'machinery' && info.isMachinerySelector) {
    return <MachinerySelector {...commonProps} />
  }
  if (fieldName === 'inputs' && info.isInputSelector) {
    return <InputsSelector {...commonProps} />
  }
  if (fieldName === 'brand' && info.isBrandSelector) {
    return <BrandField {...commonProps} />
  }
  // AGC: Numeric fields — value, amount, quantity, vehicle specs (year, hours, width, capacity)
  const NUMERIC_FIELDS = ['value', 'amount', 'quantity', 'itemQuantity', 'horimeterHours', 'year', 'width', 'tankCapacity']
  if (info.inputType === 'number' || NUMERIC_FIELDS.includes(fieldName)) {
    return <QuantityField {...commonProps} />
  }
  return <GenericField {...commonProps} onFieldSuggestionSelect={onFieldSuggestionSelect} />
}

// AGC: Build ordered list of wizard steps from fieldSuggestions
function buildSteps(
  lastResponse: VoiceProcessingResponse,
  t: (key: string, options?: object) => string,
): WizardStep[] {
  const entryType = lastResponse.entryType ?? ''
  const fs = lastResponse.fieldSuggestions ?? {}

  // AGC: Filter out system slots and entry-type-inappropriate fields
  const isExpenseRevenue = entryType === 'expense' || entryType === 'revenue'
  const visibleKeys = Object.keys(fs).filter(k => {
    if ((SYSTEM_SLOTS as readonly string[]).includes(k)) return false
    if (isExpenseRevenue && (EXPENSE_HIDDEN_SLOTS as readonly string[]).includes(k)) return false
    return true
  })

  // AGC: Inject mandatory fields that might be missing from backend response.
  // AGC: For expense/revenue the backend emits the monetary value under the `quantity`
  // AGC: slot (labeled "Valor (R$)" by smartMatchEngine). Skip injecting mandatory `value`
  // AGC: when `quantity` is already present — otherwise two identical "Valor (R$)" steps
  // AGC: render (the empty mandatory one + the populated quantity one).
  const mandatory = MANDATORY_FIELDS[entryType] ?? []
  const mandatoryFiltered = mandatory.filter(k => {
    if (visibleKeys.includes(k)) return false
    if (isExpenseRevenue && k === 'value' && visibleKeys.includes('quantity')) return false
    return true
  })
  const allKeys = [...new Set([...visibleKeys, ...mandatoryFiltered])]

  // AGC: Sort by WIZARD_STEP_ORDER priority; unknown fields go after known order
  const orderIndex = (key: string) => {
    const idx = (WIZARD_STEP_ORDER as readonly string[]).indexOf(key)
    return idx >= 0 ? idx : WIZARD_STEP_ORDER.length
  }
  allKeys.sort((a, b) => orderIndex(a) - orderIndex(b))

  // AGC: Fallback labels for ALL known fields when backend doesn't provide fieldLabel
  // AGC: Covers task, expense, revenue, field, vehicle, inventory entry types
  const FIELD_LABELS: Record<string, string> = {
    title: 'Título',
    name: 'Nome',
    category: 'Categoria',
    field: 'Talhão',
    value: 'Valor (R$)',
    amount: 'Valor (R$)',
    quantity: 'Quantidade',
    itemQuantity: 'Qtd. Física',
    itemUnit: 'Unidade',
    tankName: 'Silo / Tanque',
    brand: 'Marca',
    model: 'Modelo',
    year: 'Ano',
    horimeterHours: 'Horímetro (h)',
    width: 'Largura (m)',
    tankCapacity: 'Capacidade do Tanque (L)',
    cropType: 'Cultura',
    managementStatus: 'Status',
    priority: 'Prioridade',
    dueDate: 'Data',
    assignee: 'Responsável',
    machinery: 'Maquinário',
    inputs: 'Insumos',
    description: 'Descrição',
  }

  // AGC: Build step array — transcription first, summary last, fields in between
  const steps: WizardStep[] = [
    { fieldName: '_transcription', type: 'transcription', label: t('voice.wizardTranscription') },
    ...allKeys.map(key => {
      const info = (fs[key] as any) ?? {}
      const label = info.fieldLabel || FIELD_LABELS[key] || key
      return { fieldName: key, type: 'field' as const, label }
    }),
    { fieldName: '_summary', type: 'summary', label: t('voice.wizardSummary') },
  ]

  return steps
}

// AGC: VoiceWizardConfirmation — step-by-step card-based field entry
export const VoiceWizardConfirmation: React.FC<VoiceWizardConfirmationProps> = ({
  lastResponse,
  inputValues,
  setInputValues,
  onConfirmFields,
  onFieldSuggestionSelect,
  onClose,
  onModeSwitch,
  onPermissionBlocked,
  farms,
  selectedFarmId,
  setSelectedFarmId,
  stockToggle,
  setStockToggle,
  customFinancialCategories,
  onAddCustomCategory,
}) => {
  const { t } = useTranslation()

  const steps = buildSteps(lastResponse, t)
  const [currentStep, setCurrentStep] = useState(0)
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null)

  // AGC: Touch swipe refs for horizontal swipe navigation
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  // AGC: Navigate forward — animate slide left then advance step
  const goNext = useCallback(() => {
    if (currentStep >= steps.length - 1) return
    setSlideDirection('left')
    setTimeout(() => {
      setCurrentStep(prev => prev + 1)
      setSlideDirection(null)
    }, 200)
  }, [currentStep, steps.length])

  // AGC: Navigate back — animate slide right then retreat step
  const goBack = useCallback(() => {
    if (currentStep <= 0) return
    setSlideDirection('right')
    setTimeout(() => {
      setCurrentStep(prev => prev - 1)
      setSlideDirection(null)
    }, 200)
  }, [currentStep])

  // AGC: Swipe gesture start
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (!touch) return
    touchStartRef.current = { x: touch.clientX, y: touch.clientY }
  }

  // AGC: Swipe gesture end — navigate if horizontal swipe > 50px and mostly horizontal
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return
    const touch = e.changedTouches[0]
    if (!touch) return
    const dx = touch.clientX - touchStartRef.current.x
    const dy = Math.abs(touch.clientY - touchStartRef.current.y)
    touchStartRef.current = null
    if (Math.abs(dx) > 50 && dy < 30) {
      if (dx < 0) goNext()
      else goBack()
    }
  }

  // AGC: Guard against out-of-bounds step index (shouldn't happen but belt-and-suspenders)
  const step = steps[currentStep]
  if (!step) return null

  // AGC: Render content for current step type
  const renderStepContent = () => {
    if (step.type === 'transcription') {
      return (
        <div className="p-6 bg-white/5 border border-white/15 rounded-xl">
          <p className="text-white/50 text-sm mb-4 uppercase tracking-widest font-medium">
            {t('voice.wizardTranscription')}
          </p>
          <blockquote className="text-white/90 text-lg italic border-l-3 border-emerald-400/60 pl-5 py-3 leading-relaxed">
            &ldquo;{lastResponse.transcription || '—'}&rdquo;
          </blockquote>
          {lastResponse.entryType && (
            <p className="text-white/40 text-base mt-4">
              {lastResponse.entryType}
            </p>
          )}
        </div>
      )
    }

    if (step.type === 'summary') {
      // AGC: Summary step embeds the single-page confirmation with mode-switch hidden
      return (
        <VoiceSinglePageConfirmation
          lastResponse={lastResponse}
          inputValues={inputValues}
          setInputValues={setInputValues}
          onConfirmFields={onConfirmFields}
          onFieldSuggestionSelect={onFieldSuggestionSelect}
          onClose={onClose}
          onModeSwitch={onModeSwitch}
          onPermissionBlocked={onPermissionBlocked}
          farms={farms}
          selectedFarmId={selectedFarmId}
          setSelectedFarmId={setSelectedFarmId}
          stockToggle={stockToggle}
          setStockToggle={setStockToggle}
          customFinancialCategories={customFinancialCategories}
          onAddCustomCategory={onAddCustomCategory}
          hideModeSwitch={true}
        />
      )
    }

    // AGC: Field step — render appropriate field component
    const fs = lastResponse.fieldSuggestions ?? {}
    const info = (fs[step.fieldName] as any) ?? {}
    const commonProps: VoiceFieldProps = {
      fieldName: step.fieldName,
      info,
      inputValues,
      setInputValues,
      lastResponse,
      wizardMode: true,
      onPermissionBlocked,
    }
    return renderField(
      step.fieldName,
      info,
      commonProps,
      onFieldSuggestionSelect,
      customFinancialCategories,
      onAddCustomCategory,
    )
  }

  // AGC: Whether this step is editable (field steps, not transcription/summary)
  const isEditableStep = step.type === 'field'

  // AGC: Toggle inline text editing for current step
  const [isEditing, setIsEditing] = useState(false)

  return (
    <div className="rounded-xl overflow-hidden">
      {/* AGC: Top bar — back arrow + step dots + edit controls */}
      <div className="flex items-center gap-3 mb-4">
        {/* AGC: Back arrow — goes to previous step or closes wizard on first step */}
        <button
          onClick={currentStep > 0 ? goBack : onClose}
          className="p-2.5 -ml-1 rounded-lg text-white/60 hover:text-white/90 hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>

        {/* AGC: Step dots — bigger for touch targets */}
        <div className="flex-1 flex justify-center gap-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                i === currentStep ? 'bg-emerald-400' :
                i < currentStep ? 'bg-emerald-400/40' :
                'bg-white/20'
              }`}
            />
          ))}
        </div>

        {/* AGC: Edit controls — pencil (type) + mic (voice) on field steps — big touch targets */}
        {isEditableStep && (
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`p-3 rounded-xl transition-colors ${
                  isEditing
                    ? 'bg-emerald-500/30 text-emerald-300'
                    : 'text-white/50 hover:text-white/90 hover:bg-white/10'
                }`}
                title={t('voice.wizardTapToEdit')}
              >
                <Edit3 className="w-6 h-6" />
              </button>
              <FieldMicButton
                fieldName={step.fieldName}
                variant="default"
                className="p-3 rounded-xl text-white/50 hover:text-white/90 hover:bg-white/10 transition-colors"
                iconSize="w-6 h-6"
                onResult={(transcript) => {
                  setInputValues(prev => ({ ...prev, [step.fieldName]: transcript }))
                }}
                onPermissionBlocked={onPermissionBlocked}
              />
            </div>
            {/* AGC: Blinking hint — only shown on the first field step (step index 1) to teach users */}
            {currentStep === 1 && (
              <span className="text-emerald-300/70 text-xs animate-pulse mr-1">
                {t('voice.wizardTapToEdit')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* AGC: Step label — bigger and bolder */}
      <p className="text-center text-white/70 text-base font-medium mb-4">{step.label}</p>

      {/* AGC: Step card with slide animation — full-screen sized */}
      <div
        className={`min-h-[280px] wizard-step-content transition-all duration-200 ease-out ${
          slideDirection === 'left' ? '-translate-x-full opacity-0' :
          slideDirection === 'right' ? 'translate-x-full opacity-0' : ''
        }`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {renderStepContent()}
      </div>

      {/* AGC: Navigation buttons — only render when NOT on summary step */}
      {step.type !== 'summary' && (
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
          <button
            onClick={goBack}
            disabled={currentStep === 0}
            className="flex items-center gap-1.5 px-5 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white/80 text-base font-medium transition-colors disabled:opacity-30 min-h-[48px]"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('voice.wizardBack')}
          </button>

          <span className="text-white/40 text-sm">
            {t('voice.wizardStepOf', { current: currentStep + 1, total: steps.length })}
          </span>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={goNext}
              className="flex items-center gap-1.5 px-5 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white text-base font-medium transition-colors min-h-[48px]"
            >
              {t('voice.wizardNext')}
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : null}
        </div>
      )}

      {/* AGC: Mode toggle link — switch to single-page view */}
      <button
        onClick={onModeSwitch}
        className="w-full text-center text-white/40 text-sm mt-4 hover:text-white/60 transition-colors"
      >
        {t('voice.wizardSwitchToSingle')}
      </button>
    </div>
  )
}

export default VoiceWizardConfirmation
